import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import {
  UsersThree,
  UserPlus,
  Eye,
  Power,
  ArrowsClockwise,
  Trash,
  X,
  WhatsappLogo,
  MapPin,
  IdentificationCard,
  Buildings,
} from '@phosphor-icons/react';

const API = '/api';

const STATUS_LABELS = {
  available: 'Disponível',
  busy: 'Ocupado',
  away: 'Ausente',
  vacation: 'Férias',
  offline: 'Offline',
};

const completionBadge = (value) => {
  if (value >= 100) return 'bg-emerald-100 text-emerald-700';
  if (value >= 70) return 'bg-blue-100 text-blue-700';
  return 'bg-amber-100 text-amber-700';
};


const INVITE_LABELS = {
  pending: 'Convite pendente',
  first_access: 'Primeiro acesso',
  activated: 'Acesso ativado',
  expired: 'Convite expirado',
};

const formatAccessDate = (value) => {
  if (!value) return 'Nunca acessou';

  try {
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
      timeZone: 'America/Bahia',
    }).format(new Date(value));
  } catch {
    return 'Data indisponível';
  }
};

const GestaoCorretores = () => {
  const [brokers, setBrokers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const fetchBrokers = async () => {
    setLoading(true);

    try {
      const { data } = await axios.get(`${API}/admin/brokers`, {
        withCredentials: true,
      });

      setBrokers(data || []);
    } catch (error) {
      console.error(error);
      toast.error(
        error?.response?.data?.detail || 'Erro ao carregar corretores'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBrokers();
  }, []);

  const toggleActive = async (broker) => {
    setActionLoading(broker.id);

    try {
      await axios.patch(
        `${API}/admin/brokers/${broker.id}/status`,
        { active: !broker.active },
        { withCredentials: true }
      );

      toast.success(
        broker.active ? 'Corretor inativado' : 'Corretor ativado'
      );

      await fetchBrokers();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Erro ao alterar status'
      );
    } finally {
      setActionLoading('');
    }
  };


  const toggleDistribution = async (broker) => {
    const enabled = !broker.accepts_auto_distribution;
    setActionLoading(`distribution-${broker.id}`);

    try {
      await axios.patch(
        `${API}/admin/brokers/${broker.id}/distribution`,
        { enabled },
        { withCredentials: true }
      );

      toast.success(
        enabled
          ? 'Distribuição liberada'
          : 'Distribuição suspensa'
      );

      await fetchBrokers();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao atualizar distribuição'
      );
    } finally {
      setActionLoading('');
    }
  };

  const resetAccess = async (broker) => {
    const confirmed = window.confirm(
      `Gerar uma nova senha temporária para ${broker.name}?`
    );

    if (!confirmed) return;

    setActionLoading(`reset-${broker.id}`);

    try {
      const { data } = await axios.post(
        `${API}/admin/brokers/${broker.id}/reset-access`,
        { expiration_hours: 48 },
        { withCredentials: true }
      );

      const whatsappStatus =
        data?.invite_whatsapp?.status || 'não informado';

      window.prompt(
        `Novo convite gerado.\nWhatsApp: ${whatsappStatus}\n\nCopie a mensagem abaixo:`,
        data?.invite_message || ''
      );

      toast.success('Acesso redefinido');
      await fetchBrokers();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
        'Erro ao redefinir acesso'
      );
    } finally {
      setActionLoading('');
    }
  };

  const archiveBroker = async (broker) => {
    const confirmed = window.confirm(
      `Arquivar ${broker.name}?\n\nO histórico, os leads e os indicadores serão preservados.`
    );

    if (!confirmed) return;

    setActionLoading(broker.id);

    try {
      const { data } = await axios.delete(
        `${API}/admin/brokers/${broker.id}`,
        { withCredentials: true }
      );

      toast.success(
        data?.message || 'Corretor arquivado com sucesso'
      );

      setSelectedBroker(null);
      await fetchBrokers();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Erro ao arquivar corretor'
      );
    } finally {
      setActionLoading('');
    }
  };

  const openProfile = async (brokerId) => {
    try {
      const { data } = await axios.get(
        `${API}/admin/brokers/${brokerId}`,
        { withCredentials: true }
      );

      setSelectedBroker(data);
    } catch (error) {
      toast.error(
        error?.response?.data?.detail || 'Erro ao abrir perfil'
      );
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-indigo-100 flex items-center justify-center">
              <UsersThree
                size={32}
                weight="duotone"
                className="text-indigo-600"
              />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Gestão de Corretores
              </h1>

              <p className="text-slate-600 mt-1">
                Ative, inative, consulte perfis e preserve a governança da operação.
              </p>
            </div>
          </div>

          <Link
            to="/novo-corretor"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700"
          >
            <UserPlus size={20} />
            Novo Corretor
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200">
            <h2 className="font-bold text-slate-900">
              Corretores cadastrados
            </h2>
          </div>

          {loading ? (
            <div className="p-12 text-center text-slate-500">
              Carregando corretores...
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs uppercase text-slate-500">
                      Corretor
                    </th>
                    <th className="px-6 py-4 text-left text-xs uppercase text-slate-500">
                      Perfil
                    </th>
                    <th className="px-6 py-4 text-left text-xs uppercase text-slate-500">
                      Operação
                    </th>
                    <th className="px-6 py-4 text-left text-xs uppercase text-slate-500">
                      Leads
                    </th>
                    <th className="px-6 py-4 text-right text-xs uppercase text-slate-500">
                      Ações
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-slate-100">
                  {brokers.map((broker) => (
                    <tr key={broker.id} className="hover:bg-slate-50">
                      <td className="px-6 py-5">
                        <p className="font-semibold text-slate-900">
                          {broker.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {broker.email}
                        </p>

                        <p className="text-xs text-indigo-600 mt-1">
                          {INVITE_LABELS[broker.invite_status] ||
                            (broker.first_access_at
                              ? 'Acesso ativado'
                              : 'Cadastro legado')}
                        </p>

                        <p className="text-xs text-slate-400 mt-1">
                          Último acesso: {formatAccessDate(broker.last_login_at)}
                        </p>

                        {broker.invite_whatsapp_status && (
                          <p className="text-xs text-slate-400 mt-1">
                            Convite WhatsApp: {broker.invite_whatsapp_status}
                          </p>
                        )}
                      </td>

                      <td className="px-6 py-5">
                        <span
                          className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${completionBadge(
                            broker.profile_completion || 0
                          )}`}
                        >
                          {broker.profile_completion || 0}% preenchido
                        </span>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex items-center gap-2">
                          <span
                            className={`w-2.5 h-2.5 rounded-full ${
                              broker.active
                                ? 'bg-emerald-500'
                                : 'bg-slate-300'
                            }`}
                          />
                          <div>
                            <p className="text-sm font-medium text-slate-800">
                              {broker.active ? 'Ativo' : 'Inativo'}
                            </p>
                            <p className="text-xs text-slate-500">
                              {STATUS_LABELS[broker.operational_status] ||
                                broker.operational_status ||
                                'Não informado'}
                            </p>

                            <button
                              type="button"
                              disabled={
                                actionLoading ===
                                `distribution-${broker.id}`
                              }
                              onClick={() => toggleDistribution(broker)}
                              className={`mt-2 px-2 py-1 rounded-md text-xs font-semibold ${
                                broker.accepts_auto_distribution
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              {broker.accepts_auto_distribution
                                ? 'Distribuição ligada'
                                : 'Distribuição desligada'}
                            </button>
                          </div>
                        </div>
                      </td>

                      <td className="px-6 py-5">
                        <p className="font-semibold text-slate-900">
                          {broker.lead_count || 0}
                        </p>
                        <p className="text-xs text-slate-500">
                          {broker.active_lead_count || 0} ativos
                        </p>
                      </td>

                      <td className="px-6 py-5">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openProfile(broker.id)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50"
                          >
                            <Eye size={17} />
                            Ver perfil
                          </button>

                          <button
                            disabled={actionLoading === broker.id}
                            onClick={() => toggleActive(broker)}
                            className={`inline-flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium ${
                              broker.active
                                ? 'bg-amber-100 text-amber-700'
                                : 'bg-emerald-100 text-emerald-700'
                            }`}
                          >
                            <Power size={17} />
                            {broker.active ? 'Inativar' : 'Ativar'}
                          </button>

                          <button
                            type="button"
                            disabled={
                              actionLoading === `reset-${broker.id}`
                            }
                            onClick={() => resetAccess(broker)}
                            className="inline-flex items-center px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                            title="Redefinir acesso"
                          >
                            <ArrowsClockwise size={17} />
                          </button>

                          <button
                            disabled={actionLoading === broker.id}
                            onClick={() => archiveBroker(broker)}
                            className="inline-flex items-center px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100"
                            title="Arquivar corretor"
                          >
                            <Trash size={17} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {brokers.length === 0 && (
                    <tr>
                      <td
                        colSpan="5"
                        className="px-6 py-12 text-center text-slate-500"
                      >
                        Nenhum corretor encontrado.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {selectedBroker && (
        <div className="fixed inset-0 z-50 bg-slate-950/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-5 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-slate-900">
                  {selectedBroker.name}
                </h2>
                <p className="text-slate-500">
                  Perfil profissional do corretor
                </p>
              </div>

              <button
                onClick={() => setSelectedBroker(null)}
                className="p-2 rounded-lg hover:bg-slate-100"
              >
                <X size={23} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Perfil</p>
                  <p className="text-xl font-bold">
                    {selectedBroker.profile_completion || 0}%
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Leads</p>
                  <p className="text-xl font-bold">
                    {selectedBroker.lead_count || 0}
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Ativos</p>
                  <p className="text-xl font-bold">
                    {selectedBroker.active_lead_count || 0}
                  </p>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-500">Conta</p>
                  <p className="text-xl font-bold">
                    {selectedBroker.active ? 'Ativa' : 'Inativa'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <ProfileItem
                  icon={<WhatsappLogo size={20} />}
                  label="WhatsApp"
                  value={
                    selectedBroker.whatsapp ||
                    selectedBroker.phone ||
                    'Não informado'
                  }
                />

                <ProfileItem
                  icon={<IdentificationCard size={20} />}
                  label="CRECI"
                  value={selectedBroker.creci || 'Não informado'}
                />

                <ProfileItem
                  icon={<MapPin size={20} />}
                  label="Cidade"
                  value={selectedBroker.city || 'Não informada'}
                />

                <ProfileItem
                  icon={<Buildings size={20} />}
                  label="Status operacional"
                  value={
                    STATUS_LABELS[selectedBroker.operational_status] ||
                    selectedBroker.operational_status ||
                    'Não informado'
                  }
                />
              </div>

              <ProfileSection
                title="Regiões de atuação"
                values={selectedBroker.regions}
              />

              <ProfileSection
                title="Especialidades"
                values={selectedBroker.specialties}
              />

              <ProfileSection
                title="Empreendimentos habilitados"
                values={selectedBroker.enterprise_names}
              />

              <div>
                <p className="text-sm font-semibold text-slate-700 mb-2">
                  Apresentação profissional
                </p>
                <div className="p-4 rounded-xl bg-slate-50 text-slate-700">
                  {selectedBroker.bio || 'Apresentação ainda não preenchida.'}
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <span
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    selectedBroker.accepts_auto_distribution
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  Distribuição automática:{' '}
                  {selectedBroker.accepts_auto_distribution ? 'Sim' : 'Não'}
                </span>

                <span
                  className={`px-3 py-2 rounded-lg text-sm font-medium ${
                    selectedBroker.accepts_whatsapp_briefing
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  Briefing no WhatsApp:{' '}
                  {selectedBroker.accepts_whatsapp_briefing ? 'Sim' : 'Não'}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

const ProfileItem = ({ icon, label, value }) => (
  <div className="flex gap-3 p-4 rounded-xl border border-slate-200">
    <div className="text-indigo-600">{icon}</div>
    <div>
      <p className="text-xs uppercase text-slate-500">{label}</p>
      <p className="font-semibold text-slate-900 mt-1">{value}</p>
    </div>
  </div>
);

const ProfileSection = ({ title, values }) => (
  <div>
    <p className="text-sm font-semibold text-slate-700 mb-3">{title}</p>

    <div className="flex flex-wrap gap-2">
      {values?.length ? (
        values.map((value) => (
          <span
            key={value}
            className="px-3 py-2 rounded-lg bg-indigo-50 text-indigo-700 text-sm"
          >
            {value}
          </span>
        ))
      ) : (
        <span className="text-sm text-slate-400">
          Nenhuma informação preenchida.
        </span>
      )}
    </div>
  </div>
);

export default GestaoCorretores;
